import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Record a voice sample in the browser.
 *
 * The same device handling as `supervision/audio/use-dictation.ts` -- MIME negotiation,
 * a hard duration cap, tracks stopped rather than just the recorder -- but this one
 * hands back the audio itself instead of uploading it for transcription. The clip is
 * trimmed locally before anything leaves the page.
 */

/** Long enough to find ten good seconds in, short enough to stay a quick take. */
const MAX_DURATION_MS = 120_000;

/** What Chrome, Firefox and Safari actually produce, in the order we prefer them. */
const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

export function useVoiceRecorder() {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const releaseDevice = useCallback(() => {
    // Stop the tracks, not just the recorder: leaving them open keeps the browser's
    // recording indicator lit, which reads as "this tab is still listening to me".
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    timeoutRef.current = null;
    tickRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => releaseDevice, [releaseDevice]);

  const supported = typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices;

  const start = useCallback(async () => {
    if (!supported || recorderRef.current) return;
    setError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Microphone unavailable. Check the browser's permission for this site.");
      return;
    }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    streamRef.current = stream;
    setStream(stream);
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
    setElapsedMs(0);

    const startedAt = Date.now();
    tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 200);
    timeoutRef.current = setTimeout(() => {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    }, MAX_DURATION_MS);
  }, [supported]);

  /** Stop and hand back what was captured, or null if nothing was. */
  const stop = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return null;

    const recorded = await new Promise<Blob>((resolve) => {
      const finish = () =>
        resolve(new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" }));
      recorder.onstop = finish;
      if (recorder.state === "recording") recorder.stop();
      else finish();
    });

    releaseDevice();
    setRecording(false);
    return recorded.size > 0 ? recorded : null;
  }, [releaseDevice]);

  return { supported, recording, elapsedMs, error, start, stop, stream };
}
